import { isPlatformWeb, isPlatformMobile } from '../utils/platform';
import { Storage } from '../utils/storage';
// @ts-ignore <-- For missing module types
import branch from 'branch-sdk';
import { BranchDeepLinks, BranchInitEvent } from 'capacitor-branch-deep-links';

// Constants
const PENDING_REFERRAL_CODE_KEY = 'pendingReferralCode';
const REFRESHING_FOR_REFERRAL_KEY = 'refreshingForReferral';

/**
 * Cross-platform referral service to handle Branch SDK initialization and referral code management
 */
export class ReferralService {
  private static instance: ReferralService;
  private initialized = false;

  /**
   * Get the singleton instance of ReferralService
   */
  public static getInstance(): ReferralService {
    if (!ReferralService.instance) {
      ReferralService.instance = new ReferralService();
    }
    return ReferralService.instance;
  }

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Check if the current device is a desktop browser
   */
  private isDesktopBrowser(): boolean {
    if (!isPlatformWeb()) return false;
    
    // Check for common mobile user agents
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = [
      'android',
      'webos',
      'iphone',
      'ipad',
      'ipod',
      'blackberry',
      'windows phone'
    ];
    
    return !mobileKeywords.some(keyword => userAgent.includes(keyword));
  }

  /**
   * Initialize the Branch SDK
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Skip initialization for desktop browsers
    if (this.isDesktopBrowser()) {
      console.log('[Branch SDK] Skipping initialization on desktop browser');
      this.initialized = true;
      return;
    }

    if (isPlatformWeb()) {
      await this.initializeWeb();
    } else if (isPlatformMobile()) {
      await this.initializeMobile();
    }

    this.initialized = true;
  }

  /**
   * Initialize Branch SDK for web
   */
  private async initializeWeb(): Promise<void> {
    return new Promise<void>((resolve) => {
      const branchKey = import.meta.env.VITE_BRANCH_KEY;
      if (!branchKey) {
        console.warn('[Branch SDK] Branch Key not found in environment variables (VITE_BRANCH_KEY).');
        resolve();
        return;
      }

      branch.init(branchKey, async (err: any, data: any) => {
        if (err) {
          console.error('Branch initialization error:', err);
          resolve();
          return;
        }

        // Check if deep link data is available
        if (data && data.data_parsed) {
          const parsed = data.data_parsed;
          const refCode = parsed.ref_code;
          if (refCode) {
            await this.handleReferralCode(refCode);
          }
          // Dispatch product deep link event if present
          const itemId = parsed.item_id || 
                        parsed.$deeplink_path?.split('/')?.[1] || 
                        parsed.id;  // Add support for query parameter format ?id=123
          if (itemId) {
            const productEvent = new CustomEvent('productDeepLinkReceived', { detail: { itemId } });
            window.dispatchEvent(productEvent);
          }
        } else {
          console.log('[Branch SDK] No deep link data found on init.');
        }
        resolve();
      });
    });
  }

  /**
   * Initialize Branch SDK for mobile via Capacitor plugin
   */
  private async initializeMobile(): Promise<void> {
    console.log('[Branch SDK] Starting plugin integration mode');

    // Listen for plugin init events
    BranchDeepLinks.addListener('init', (event: BranchInitEvent) => {
      console.log('[Branch Plugin] init event received:', event);
      console.log('[Branch Plugin] referringParams:', JSON.stringify(event.referringParams));
      const branchData = event.referringParams;
      
      // Extract and validate referral code
      const refCode = branchData?.ref_code || branchData?.referral_code || branchData?.['+referrer'];
      console.log('[Branch Plugin] Extracted referral code:', refCode);
      
      if (refCode && this.isValidReferralCode(refCode)) {
        this.handleReferralCode(refCode);
      }
      // Dispatch product deep link event if present
      const itemId = branchData?.item_id || 
                    branchData?.['$deeplink_path']?.split('/')?.[1] || 
                    branchData?.['id'];  // Add support for query parameter format ?id=123
      
      console.log('[Branch Plugin] Extracted item ID:', itemId);
      console.log('[Branch Plugin] Raw item_id:', branchData?.item_id);
      console.log('[Branch Plugin] Raw $deeplink_path:', branchData?.['$deeplink_path']);
      console.log('[Branch Plugin] Raw id param:', branchData?.['id']);
      
      if (itemId) {
        const productEvent = new CustomEvent('productDeepLinkReceived', { detail: { itemId } });
        window.dispatchEvent(productEvent);
        console.log('[Branch Plugin] Dispatched productDeepLinkReceived event with itemId:', itemId);
      } else {
        console.log('[Branch Plugin] No item ID found in deep link data');
      }
    });

    // Listen for plugin init errors
    BranchDeepLinks.addListener('initError', (error: any) => {
      console.error('[Branch Plugin] initError:', error);
    });
  }

  /**
   * Validates if a referral code is in the correct format (numeric)
   * @param code The referral code to validate
   * @returns Boolean indicating if the code is valid
   */
  private isValidReferralCode(code: string): boolean {
    return /^\d+$/.test(code);
  }

  /**
   * Handle a referral code from Branch SDK
   * @param code Referral code from Branch deep link
   */
  private async handleReferralCode(code: string): Promise<void> {
    // Skip referral code handling on desktop browsers
    if (this.isDesktopBrowser()) {
      console.log('[Branch SDK] Skipping referral code handling on desktop browser');
      return;
    }

    // Additional validation to ensure code is numeric
    if (!this.isValidReferralCode(code)) {
      console.error('[Branch SDK] Invalid referral code format, must be numeric:', code);
      return;
    }

    console.log('[Branch SDK] Deep link contained referral code:', code);

    // Check if we've already processed this referral code
    const existingCode = await Storage.get(PENDING_REFERRAL_CODE_KEY);
    if (existingCode === code) {
      console.log('[Branch SDK] Referral code already in storage, skipping duplicate processing');
      return;
    }

    // Store it for the Signup page to pick up
    await Storage.set(PENDING_REFERRAL_CODE_KEY, code);

    // Dispatch a custom event to notify components that referral data has arrived
    const referralEvent = new CustomEvent('referralDataReceived', {
      detail: { referralCode: code }
    });
    window.dispatchEvent(referralEvent);
    console.log('[Branch SDK] Dispatched referralDataReceived event');
  }

  /**
   * Get the pending referral code from storage
   * @returns The referral code or null if not found
   */
  public async getPendingReferralCode(): Promise<string | null> {
    return Storage.get(PENDING_REFERRAL_CODE_KEY);
  }

  /**
   * Set the pending referral code in storage
   * @param code The referral code to store
   */
  public async setPendingReferralCode(code: string): Promise<void> {
    return Storage.set(PENDING_REFERRAL_CODE_KEY, code);
  }

  /**
   * Set a flag to indicate we're refreshing the page to capture referral data
   */
  public async setRefreshingForReferral(): Promise<void> {
    return Storage.set(REFRESHING_FOR_REFERRAL_KEY, 'true');
  }

  /**
   * Check if we're refreshing the page for referral data
   */
  public async isRefreshingForReferral(): Promise<boolean> {
    const value = await Storage.get(REFRESHING_FOR_REFERRAL_KEY);
    return value === 'true';
  }

  /**
   * Clear the refreshing flag
   */
  public async clearRefreshingFlag(): Promise<void> {
    return Storage.remove(REFRESHING_FOR_REFERRAL_KEY);
  }

  /**
   * Remove the pending referral code from storage
   */
  public async clearPendingReferralCode(): Promise<void> {
    return Storage.remove(PENDING_REFERRAL_CODE_KEY);
  }
} 